/**
 * The PC a Dispatch thread belongs to, in the header, and the way to the
 * others: the account's PCs, the latest used first, each with its platform,
 * whether it is online and when it was last seen. Choosing one opens its
 * thread. A PC can be forgotten from the list, behind a confirmation that
 * says its thread goes with it. In the Android app, a phone not yet paired
 * with the PC is offered pairing, which opens the phone's scanner.
 */

import { useRef, useState } from 'react';
import { forgetDevice, type Device } from '../api';
import { ds } from '../ds';
import { platformIcon, seenText } from '../hooks/deviceLists';
import { useRouter } from '../router';
import { useApp } from '../state';
import { IconTap, Menu, MenuDivider, MenuItem, Ph, useDismiss } from './ui';

/** Whether a PC is on: filled when it is, a ring when it is not. */
export function PcDot({ online }: { online: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 8,
        height: 8,
        flex: 'none',
        boxSizing: 'border-box',
        borderRadius: 999,
        background: online ? 'var(--success-500)' : 'transparent',
        border: online ? 0 : '1.5px solid var(--text-faint)'
      }}
    />
  );
}

export function DevicePicker({ current, name, onPair }: { current: Device | null; name?: string; onPair?: () => void }) {
  const { devices, removeDevice } = useApp();
  const { navigate } = useRouter();
  const { Button, Dialog } = ds();
  const [open, setOpen] = useState(false);
  const [forgetting, setForgetting] = useState<Device | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const now = Math.floor(Date.now() / 1000);

  useDismiss(root, open && !forgetting, () => setOpen(false));
  const cancel = () => {
    if (!busy) setForgetting(null);
  };
  // The design system's Dialog has no Escape of its own.
  useDismiss(dialog, !!forgetting, cancel);

  const forget = async () => {
    if (!forgetting) return;
    setBusy(true);
    setError(null);
    try {
      await forgetDevice(forgetting.id);
      removeDevice(forgetting.id);
      const wasCurrent = forgetting.id === current?.id;
      setForgetting(null);
      setOpen(false);
      if (wasCurrent) navigate('/dispatch');
    } catch {
      setError('That did not go through. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={root} style={{ position: 'relative', minWidth: 0 }}>
      <button
        type="button"
        className="bare hov-tint"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={current ? `${current.name}, ${current.online ? 'online' : 'offline'}. Choose a PC` : 'Choose a PC'}
        title="Choose a PC"
        onClick={() => setOpen((o) => !o)}
        style={{
          maxWidth: '100%',
          height: 34,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 8px',
          borderRadius: 'var(--radius-chrome)',
          color: 'var(--text-strong)'
        }}
      >
        {current ? <Ph name={platformIcon(current.platform)} size={16} color="var(--text-muted)" style={{ flex: 'none' }} /> : null}
        <span style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
          {current?.name ?? name ?? 'Dispatch'}
        </span>
        {current ? <PcDot online={current.online} /> : null}
        <Ph name="caret-down" size={12} color="var(--text-faint)" style={{ flex: 'none' }} />
      </button>

      {open ? (
        <Menu style={{ left: 0, top: 'calc(100% + 6px)', width: 320, maxWidth: 'calc(100vw - 32px)' }}>
          {(devices ?? []).map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <button
                type="button"
                role="menuitem"
                className="bare hov-row"
                onClick={() => {
                  setOpen(false);
                  navigate(`/dispatch/${d.id}`);
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 44,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '0 9px',
                  borderRadius: 6,
                  textAlign: 'left',
                  background: d.id === current?.id ? 'var(--border-subtle)' : 'transparent'
                }}
              >
                <Ph name={platformIcon(d.platform)} size={16} color="var(--text-muted)" style={{ flex: 'none' }} />
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{seenText(d, now)}</span>
                </span>
                <PcDot online={d.online} />
              </button>
              <IconTap icon="trash" label={`Forget ${d.name}`} size={32} glyph={14} onClick={() => (setError(null), setForgetting(d))} />
            </div>
          ))}
          {onPair ? (
            <>
              <MenuDivider />
              <MenuItem
                icon="qr-code"
                label="Pair this phone"
                onSelect={() => {
                  setOpen(false);
                  onPair();
                }}
                style={{ minHeight: 44 }}
              />
            </>
          ) : null}
        </Menu>
      ) : null}

      {forgetting ? (
        <div ref={dialog} style={{ display: 'contents' }}>
          <Dialog
            open
            position="fixed"
            width={420}
            title={`Forget ${forgetting.name}?`}
            onClose={cancel}
            style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16 }}
            footer={
              <>
                <Button variant="ghost" density="app" size="sm" autoFocus disabled={busy} onClick={cancel}>
                  Cancel
                </Button>
                <Button variant="danger" density="app" size="sm" disabled={busy} onClick={() => void forget()}>
                  Forget
                </Button>
              </>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-body)' }}>
                {forgetting.online
                  ? 'Its thread is deleted too. While Dispatch is on there, it comes straight back with a new thread.'
                  : 'Its thread is deleted too.'}
              </p>
              {error ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error}</span> : null}
            </div>
          </Dialog>
        </div>
      ) : null}
    </div>
  );
}
